import { schnorr } from '@noble/curves/secp256k1.js';
import { sha256   } from '@noble/hashes/sha2.js';

const TOKEN_KEY = 'dtmedia:token';

function toHex(bytes) {
  return Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('');
}

async function sha256Hex(str) {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(str));
  return toHex(new Uint8Array(buf));
}

const B32_CHARSET = 'qpzry9x8gf2tvdw0s3jn54khce6mua7l';

function bech32Decode(str) {
  const s = str.toLowerCase();
  const sep = s.lastIndexOf('1');
  if (sep < 1) throw new Error('Invalid bech32 string');
  const data5 = [];
  for (let i = sep + 1; i < s.length - 6; i++) {
    const v = B32_CHARSET.indexOf(s[i]);
    if (v < 0) throw new Error('Invalid bech32 character: ' + s[i]);
    data5.push(v);
  }
  let acc = 0, bits = 0;
  const bytes = [];
  for (const val of data5) {
    acc = (acc << 5) | val;
    bits += 5;
    while (bits >= 8) { bits -= 8; bytes.push((acc >> bits) & 0xff); }
  }
  return new Uint8Array(bytes);
}

async function submitAuthEvent(event) {
  const resp = await fetch('/api/auth/verify', {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify({ event }),
  });
  const data = await resp.json().catch(() => ({}));
  if (!resp.ok) throw new Error(data.error || `Login failed (${resp.status})`);
  localStorage.setItem(TOKEN_KEY, data.token);
  return data.token;
}

function makeEventTemplate(pubkey) {
  return {
    kind:       27235,
    pubkey,
    created_at: Math.floor(Date.now() / 1000),
    tags:       [['u', window.location.origin]],
    content:    'dtmedia login',
  };
}

export async function loginWithNostr() {
  if (!window.nostr) throw new Error('Nostr extension not found. Please install Alby.');
  const pubkey   = await window.nostr.getPublicKey();
  const template = makeEventTemplate(pubkey);
  const serialized = JSON.stringify([0, template.pubkey, template.created_at, template.kind, template.tags, template.content]);
  template.id = await sha256Hex(serialized);
  const signed = await window.nostr.signEvent(template);
  return submitAuthEvent(signed);
}

export async function loginWithNsec(nsecStr) {
  const nsec = nsecStr.trim();
  if (!nsec.startsWith('nsec1')) throw new Error('Expected an nsec key (starts with nsec1)');
  const privBytes = bech32Decode(nsec);
  if (privBytes.length !== 32) throw new Error('Invalid nsec: unexpected key length');
  const pubBytes = schnorr.getPublicKey(privBytes);
  const pubkey   = toHex(pubBytes);
  const template = makeEventTemplate(pubkey);
  const serialized = JSON.stringify([0, template.pubkey, template.created_at, template.kind, template.tags, template.content]);
  const hashBytes  = sha256(new TextEncoder().encode(serialized));
  const sigBytes   = schnorr.sign(hashBytes, privBytes);
  return submitAuthEvent({ ...template, id: toHex(hashBytes), sig: toHex(sigBytes) });
}

export function getToken()    { return localStorage.getItem(TOKEN_KEY); }
export function logout()      { localStorage.removeItem(TOKEN_KEY); }
export function authHeaders() {
  const t = getToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}

export async function authFetch(url, options = {}) {
  const resp = await fetch(url, { ...options, headers: { ...authHeaders(), ...options.headers } });
  if (resp.status === 401) { logout(); window.location.reload(); }
  return resp;
}
